import { ShapeAttrs } from '@antv/g-base';
import { Text } from '@antv/g-canvas/lib/shape';

import { Group, Shape } from 'ui/workflows/flowchart/types';
import {
  DEFAULT_FONT,
  DEFAULT_FONT_SIZE,
  DEFAULT_FONT_STYLE,
  DEFAULT_FONT_WEIGHT,
  DEFAULT_LINE_HEIGHT,
  ShapeType,
} from '../../constants';
import { getClassNameFromShape, isGroup } from '../helpers';

const canvas = document.createElement('canvas');
const canvasContext = canvas.getContext('2d') as CanvasRenderingContext2D;

export const getFontString = ({
  fontStyle = DEFAULT_FONT_STYLE,
  fontSize = DEFAULT_FONT_SIZE,
  lineHeight = DEFAULT_LINE_HEIGHT,
  fontFamily = DEFAULT_FONT,
  fontWeight = DEFAULT_FONT_WEIGHT,
}: ShapeAttrs = {}): string =>
  `${fontStyle} ${fontWeight} ${fontSize}px/${lineHeight}px ${fontFamily}`;

export const setFont = (config: ShapeAttrs = {}) => {
  canvasContext.font = getFontString(config);
};
export const setFontBackToDefault = () => setFont();

// Initiate the inital font size
setFontBackToDefault();

/**
 * Gets the dimensions that a text string would be on canvas
 */
export const getTextDimensions = (text = '', fontConfig: ShapeAttrs = {}) => {
  if (typeof text !== 'string') return { width: 0, height: 0 };

  setFont(fontConfig);

  const dimensions = canvasContext.measureText(text);
  const lineHeight = fontConfig.lineHeight || DEFAULT_LINE_HEIGHT;
  let maxWidth = 0;
  let height = 0;

  // Canvas measures \n as actual characters, so you have to manually
  // split and measure
  text.split('\n').forEach((t) => {
    const d = canvasContext.measureText(t);
    maxWidth = Math.max(maxWidth, d.width);
    height += lineHeight;
  });

  setFontBackToDefault();

  return { ...dimensions, width: maxWidth, height };
};

export const getTextWidth = (text = '', fontConfig: ShapeAttrs = {}): number =>
  getTextDimensions(text, fontConfig).width;

export const isTextShape = (shape: Shape): shape is Text => shape?.cfg?.type === 'text';

export interface GetAssociatedShapeName {
  /** The shape */
  shape: Shape;
  /**
   * The classname of the associated shape.
   * If not provided, will be determined by the shape.
   */
  className?: string;
  /** The default if className cannot be determined */
  defaultClassName?: string;
  /** The suffix to add */
  suffix?: string;
}

export const getAssociatedShapeName = ({
  shape,
  className,
  defaultClassName = 'text',
  suffix,
}: GetAssociatedShapeName): string =>
  className || `${getClassNameFromShape(shape) || defaultClassName}${suffix ? `-${suffix}` : ''}`;

export const getTextShapeDimensions = (shape: Shape) =>
  !!shape?.cfg?.visible && shape?.cfg?.attrs?.shapeType !== ShapeType.Underline
    ? shape.getBBox()
    : { width: 0, height: 0 };

export const getTextShapeWidth = (shape: Shape): number => getTextShapeDimensions(shape).width;
export const getTextShapeHeight = (shape: Shape): number => getTextShapeDimensions(shape).height;
export const getTextGroupWidth = (group: Group): number =>
  group.getChildren().reduce((acc, c) => acc + getTextShapeWidth(c as Shape), 0);
/**
 * As we're currently assuming one line, we're taking the max height
 * If we move to multiline, this will change
 */
export const getTextGroupHeight = (group: Group): number =>
  group.getChildren().reduce((acc, c) => Math.max(acc, getTextShapeHeight(c as Shape)), 0);

export const getTextItemWidth = (item: Group | Shape): number => {
  if (!item) return 0;
  return isGroup(item) ? getTextGroupWidth(item) : getTextShapeWidth(item);
};

export const getTextItemHeight = (item: Group | Shape): number => {
  if (!item) return 0;
  return isGroup(item) ? getTextGroupHeight(item) : getTextShapeHeight(item);
};
